// BatchViewModal.jsx

import React, { useState, useRef, useEffect } from "react";
import "./BatchViewModal.scss";
import BaseDrawer from "../UI/BaseDrawer/BaseDrawer";
import CustomForm from "../UI/CustomForm/CustomForm";
import BaseCard from "../UI/BaseCard/BaseCard";
import { editBatch } from "../../util/API/batch";
import PinPad from "../PinPad/PinPad";
import CustomAlert from "../UI/CustomAlert/CustomAlert";

const READONLY_FIELDS = ["inputfilename", "categoryid"];

function BatchViewModal({ open, closeModal, selectedBatch, getHistoryList }) {
  const [showPinPad, setShowPinPad] = useState(false);
  const [submittedFormData, setSubmittedFormData] = useState(null);
  const formWrapperRef = useRef(null);
  const observerRef = useRef(null);
  const [alert, setAlert] = useState({
    showAlert: false,
    severity: "error",
    header: "",
    message: "",
  });

  useEffect(() => {
    if (!open) return;

    // Keep trying to disable fields every 100ms for up to 3 seconds
    // This handles async form JSON fetch + React re-renders
    let attempts = 0;
    const MAX_ATTEMPTS = 30;

    const tryDisable = () => {
      if (!formWrapperRef.current) return;

      READONLY_FIELDS.forEach((name) => {
        const els = formWrapperRef.current.querySelectorAll(
          `input[name="${name}"], select[name="${name}"], textarea[name="${name}"]`
        );

        els.forEach((el) => {
          if (!el.disabled) {
            el.disabled = true;

            // Grey out the MUI fieldset visually
            const muiBase = el.closest(".MuiInputBase-root");
            if (muiBase) {
              muiBase.classList.add("Mui-disabled");
              muiBase.setAttribute("style",
                "pointer-events: none; background-color: #f5f5f5;"
              );
              // Force the inner input text color
              el.style.setProperty("-webkit-text-fill-color", "#9e9e9e", "important");
              el.style.color = "#9e9e9e";
            }

            const muiLabel = muiBase
              ?.closest(".MuiFormControl-root")
              ?.querySelector("label");
            if (muiLabel) {
              muiLabel.classList.add("Mui-disabled");
            }
          }
        });
      });

      attempts++;
    };

    const interval = setInterval(() => {
      tryDisable();
      if (attempts >= MAX_ATTEMPTS) clearInterval(interval);
    }, 100);

    // Also use MutationObserver as a belt-and-suspenders approach
    if (formWrapperRef.current) {
      observerRef.current = new MutationObserver(tryDisable);
      observerRef.current.observe(formWrapperRef.current, {
        childList: true,
        subtree: true,
        attributes: false,
      });
    }

    return () => {
      clearInterval(interval);
      observerRef.current?.disconnect();
    };
  }, [open]);

  const handleConfirm = (event, pin) => {
    const PIN = pin.join("");
    handleSubmit(event, PIN);
    setShowPinPad(false);
  };

  const handlePinPad = (data, formData) => {
    setSubmittedFormData({ data, formData });
    setShowPinPad(true);
  };

  const handleSubmit = async (e, PIN) => {
    if (!submittedFormData) return;
    const { data } = submittedFormData;
    const payload = { ...data, PIN };

    try {
      const response = await editBatch(
        payload?.requestuserid,
        payload?.requestid,
        PIN,
        payload?.scheduletime,
        payload?.notes
      );
      if (response?.callStatus === "1") {
        setAlert({
          showAlert: true,
          severity: "success",
          header: "Success",
          message: "Batch Entry Updated Successfully",
        });
        getHistoryList();
      } else {
        setAlert({
          showAlert: true,
          severity: "error",
          message: "Batch Upload was not updated successfully",
          header: "Error!",
        });
      }
    } catch (error) {
      console.log(error);
    }
  };

  return (
    <BaseDrawer
      open={open}
      handleClose={closeModal}
      height="90vh"
      title={"Edit Batch Entry"}
    >
      <BaseCard>
        <div className="batch-edit-form-wrapper" ref={formWrapperRef}>
          <CustomForm
            formName={"editBatchEntry"}
            buttonText={"Submit"}
            onSubmit={handlePinPad}
            loadingButtonText={"Loading ..."}
            formValues={selectedBatch}
          />
        </div>
      </BaseCard>

      {showPinPad && (
        <PinPad
          show={showPinPad}
          handleConfirm={handleConfirm}
          onClick={() => setShowPinPad(false)}
        />
      )}

      <CustomAlert
        severity={alert.severity}
        show={alert.showAlert}
        header={alert.header}
        message={alert.message}
        onClick={(prev) => {
          setAlert({ ...prev, showAlert: false });
          closeModal();
        }}
        autoclose={alert.severity === "error" ? false : true}
      />
    </BaseDrawer>
  );
}

export default BatchViewModal;